'use client';

import { Divider, Link, RadioButton, RadioGroup, Stack, Typography, useBreakpoints } from '@castlery/fortress';
import {
  Combination,
  OptionType,
  OptionValue,
  Product,
  ProductOptionValueConfig,
  useLazyGetVariantByVariantIdQuery,
  Variant,
} from '@castlery/modules-product-domain';
import { usePathname, useSearchParams } from 'next/navigation';

import { ArrowForwardIos } from '@castlery/fortress/Icons';
import { FortressImage, OptionSelector, PinchZoomViewer } from '@castlery/shared-components';
import { createUrl } from '@castlery/utils';
import { useCallback, useMemo, useState } from 'react';
import { useCombination } from '../../../../hooks/use-combination';
import { ProductFabricTooltip } from './product-fabrc-tooltip';
import { ProductFabricDrawer } from './product-fabric-drawer';
import { ProductFreeSwatchDrawer } from './product-free-swatch-drawer';
import { EVENT_PDP_CONFIGURATION } from '@castlery/modules-tracking-services';
import { useAppDispatch } from '@castlery/shared-redux-store';

interface ProductOptionsValuesProps {
  sortOptionType: OptionType;
  product: Product;
  variant: Variant | undefined;
}

// Display mode configuration
export const displayMode = {
  material: 'circle',
  leg_color: 'circle',
  wood: 'circle',
  color_option: 'circle',
  frame: 'circle',
  orientation: 'rectangle',
};

export const ProductOptionsValues = (props: ProductOptionsValuesProps) => {
  const dispatch = useAppDispatch();
  const { sortOptionType, product, variant } = props;
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { desktop } = useBreakpoints();
  const [openFabricDrawer, setOpenFabricDrawer] = useState(false);
  const [openFreeSwatchDrawer, setOpenFreeSwatchDrawer] = useState(false);
  const [openDimensionImagePinchZoom, setOpenDimensionImagePinchZoom] = useState(false);
  const targetLinks = useMemo(() => {
    return variant?.dimension_image?.links || product?.dimension_image?.links;
  }, [product, variant]);
  const combinations: Combination[] = useCombination({
    customizations: product?.customizations,
    optionTypes: product?.option_types,
  });
  const [getVariantByVariantId, { isFetching }] = useLazyGetVariantByVariantIdQuery();

  const handleTrack = useCallback(
    async ({ action, label, skuName, skuId }: { action: string; label: string; skuName: string; skuId: string }) => {
      await dispatch(
        EVENT_PDP_CONFIGURATION({
          action,
          label,
          skuName,
          skuId,
        })
      );
    },
    []
  );

  const clickHandler = useCallback(
    async (currentOptionVariantId: number | undefined, optionValue: OptionValue, optionName: string) => {
      if (!currentOptionVariantId) {
        currentOptionVariantId = combinations.find((combination) => {
          return combination[sortOptionType?.name] === optionValue?.name;
        })?.id;
        if (!currentOptionVariantId) return;
      }
      try {
        const res = await getVariantByVariantId(currentOptionVariantId, false).unwrap();

        const optionSearchParams = new URLSearchParams(searchParams.toString());
        if (res) {
          handleTrack({
            action: optionName,
            label: optionValue?.presentation,
            skuName: res?.name,
            skuId: res?.sku,
          });
          for (const { option_type_name, name } of res.variant_option_values) {
            optionSearchParams.set(option_type_name, name);
          }
        }
        optionSearchParams.set(optionName, optionValue?.name);
        const optionUrl = createUrl(pathname, optionSearchParams);
        window.history.replaceState(null, '', optionUrl);
      } catch (error) {
        console.log('🚀 ~ clickHandler ~ error:', error);
      }
    },
    [combinations, getVariantByVariantId, pathname, searchParams, sortOptionType?.name]
  );

  const sortOptionValues = useMemo(() => {
    const optionOrderMap = new Map();
    if (combinations) {
      combinations.forEach((combination, index) => {
        const optionValueName = combination[sortOptionType.name];
        if (optionValueName && !optionOrderMap.has(optionValueName)) {
          optionOrderMap.set(optionValueName, index);
        }
      });
    }

    let values = sortOptionType?.values
      ?.filter(({ name }) => combinations?.find((combination) => combination[sortOptionType.name] === name))
      ?.map((optionValue) => {
        const optionName = sortOptionType?.name;
        const optionSearchParams = new URLSearchParams(searchParams?.toString());
        if (variant) {
          for (const { option_type_name, name } of variant.variant_option_values) {
            optionSearchParams.set(option_type_name, name);
          }
        }
        const isSelected = optionSearchParams.get(optionName) === optionValue?.name;
        optionSearchParams.set(optionName, optionValue?.name);
        const filtered = Array.from(optionSearchParams?.entries())?.filter(([key, value]) =>
          product?.option_types.find(
            (option) => option.name === key && option.values.find(({ name }) => name === value)
          )
        );
        const currentOptionVariantCombination = combinations?.find((combination) =>
          filtered?.every(([key, value]) => combination[key] === value)
        );
        const currentOptionVariantId = currentOptionVariantCombination?.id;

        const result: ProductOptionValueConfig = {
          id: optionValue?.id?.toString(),
          name: optionValue?.name,
          value: optionValue?.presentation,
          label: optionValue?.presentation,
          image: optionValue?.image_url,
          collection: optionValue?.collection,
          isSelected,
          isCustomized: currentOptionVariantCombination?.is_customized || false,
          onSelect: () => clickHandler(currentOptionVariantId, optionValue, optionName),
        };

        const allCombinationsWithThisValue = combinations?.filter((combination) => {
          return combination[sortOptionType?.name] === optionValue?.name;
        });

        const customizedCombinations = allCombinationsWithThisValue?.filter((combination) => {
          return combination.is_customized;
        });

        if (customizedCombinations?.length === allCombinationsWithThisValue?.length) {
          result.isCustomized = true;
        }

        return result;
      })
      ?.sort((a, b) => {
        const aOrder = optionOrderMap.get(a.name) ?? Number.MAX_SAFE_INTEGER;
        const bOrder = optionOrderMap.get(b.name) ?? Number.MAX_SAFE_INTEGER;
        return aOrder - bOrder;
      });

    // Sort orientation values with 'left' items first
    if (sortOptionType.name === 'orientation' && values) {
      values = values.sort((a, b) => {
        const aHasLeft = a.value?.toLowerCase().includes('left');
        const bHasLeft = b.value?.toLowerCase().includes('left');
        if (aHasLeft && !bHasLeft) return -1;
        if (!aHasLeft && bHasLeft) return 1;
        return 0;
      });
    }

    return values;
  }, [
    clickHandler,
    combinations,
    product?.option_types,
    searchParams,
    sortOptionType.name,
    sortOptionType?.values,
    variant,
  ]);

  const handleSelect = useCallback(
    (optionId: string) => {
      const selectedOption = sortOptionValues?.find((option) => option.id === optionId);
      if (selectedOption) {
        selectedOption.onSelect();
      }
    },
    [sortOptionValues]
  );

  const stockedOptions = useMemo(
    () => sortOptionValues?.filter(({ isCustomized }) => !isCustomized) || [],
    [sortOptionValues]
  );
  const customOptions = useMemo(
    () => sortOptionValues?.filter(({ isCustomized }) => isCustomized) || [],
    [sortOptionValues]
  );

  const freeSwatch = useMemo(() => product?.show_free_swatch, [product]);

  return (
    <>
      {sortOptionType.name === 'material' && (
        <Stack>
          {stockedOptions.length > 0 && (
            <Stack>
              <Typography level="caption1" my={3}>
                Stocked Fabrics:
              </Typography>
              <OptionSelector
                options={stockedOptions}
                onSelect={handleSelect}
                maxDisplay={20}
                showAdditionalCount={false}
                sx={{
                  flexWrap: 'wrap',
                }}
                isProductPage={true}
                isNeedTooltip={desktop ? true : false}
                TooltipContent={ProductFabricTooltip}
              />
            </Stack>
          )}
          {customOptions.length > 0 && (
            <Stack>
              <Typography level="caption1" mt={3} mb={1}>
                Custom Fabrics:
              </Typography>
              <Typography level="caption2" mb={3}>
                Create a piece made just for you in one of our custom fabrics.
              </Typography>
              <OptionSelector
                options={customOptions}
                onSelect={handleSelect}
                maxDisplay={20}
                showAdditionalCount={false}
                sx={{
                  flexWrap: 'wrap',
                }}
                isProductPage={true}
                isNeedTooltip={desktop ? true : false}
                TooltipContent={ProductFabricTooltip}
              />
            </Stack>
          )}
          <Stack direction="row" mt={4}>
            <Link component="button" level="body1" variant="secondary" onClick={() => setOpenFabricDrawer(true)}>
              View fabric details
            </Link>
            {freeSwatch && (
              <>
                <Divider orientation="vertical" sx={{ mx: 3 }} />
                <Link
                  component="button"
                  level="body1"
                  variant="secondary"
                  data-selenium="open_swatch"
                  onClick={() => setOpenFreeSwatchDrawer(true)}
                >
                  Get free swatches
                </Link>
              </>
            )}
          </Stack>
          <ProductFabricDrawer
            open={openFabricDrawer}
            onClose={() => setOpenFabricDrawer(false)}
            options={sortOptionValues || []}
            selectedOption={sortOptionValues?.find((option) => option.isSelected)}
          />
          <ProductFreeSwatchDrawer open={openFreeSwatchDrawer} onClose={() => setOpenFreeSwatchDrawer(false)} />
        </Stack>
      )}

      {sortOptionType.name !== 'material' && (
        <>
          {(() => {
            const currentDisplayMode = displayMode[sortOptionType.name as keyof typeof displayMode] || 'rectangle';

            if (currentDisplayMode === 'circle') {
              return (
                <OptionSelector
                  options={sortOptionValues || []}
                  onSelect={handleSelect}
                  maxDisplay={20}
                  showAdditionalCount={false}
                  sx={{
                    flexWrap: 'wrap',
                    mt: 3,
                  }}
                  isProductPage={true}
                />
              );
            } else {
              return (
                <RadioGroup
                  name="configurable-options"
                  value={sortOptionValues?.find((option) => option.isSelected)?.id || ''}
                  onChange={(event) => {
                    const selectedOption = sortOptionValues?.find((option) => option.id === event.target.value);
                    selectedOption?.onSelect();
                  }}
                >
                  <Stack direction="row" flexWrap="wrap" mt={3} gap={3}>
                    {sortOptionValues?.map((option) => (
                      <RadioButton
                        key={option.id}
                        label={
                          option.image ? (
                            <FortressImage
                              src={option.image}
                              alt={option.label}
                              sx={{ width: 101, height: 40, '--AspectRatio-paddingBottom': 0 }}
                              sizes={101 + 'px'}
                            />
                          ) : (
                            option.label
                          )
                        }
                        value={option.id}
                        sx={{
                          ...(option.image && {
                            px: 2,
                            py: 1,
                          }),
                        }}
                      />
                    ))}
                  </Stack>
                </RadioGroup>
              );
            }
          })()}
          {sortOptionType.presentation === 'Size' && (
            <>
              {targetLinks && (
                <Link
                  component="button"
                  variant="secondary"
                  level="body1"
                  mt={3}
                  py={2}
                  onClick={() => setOpenDimensionImagePinchZoom(true)}
                  endDecorator={
                    <ArrowForwardIos
                      sx={{
                        width: '20px',
                        height: '20px',
                      }}
                    />
                  }
                >
                  Size Guide
                </Link>
              )}
            </>
          )}
          <PinchZoomViewer
            open={openDimensionImagePinchZoom}
            setOpen={setOpenDimensionImagePinchZoom}
            slideImages={[
              {
                width: 100,
                height: 100,
                alt: 'dimension image of product',
                src: targetLinks?.large_gray || '',
              },
            ]}
            index={0}
          />
        </>
      )}
    </>
  );
};
